import { type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type DocumentDetailResponse,
  type DocumentResultData,
  type DocumentSummary,
  type ExtractedFieldData,
  type Provenance,
  listDocuments,
  getDocument,
  exportDocuments,
  downloadDocument,
  updateDocumentFields,
  deleteDocument,
  restoreDocument,
  fetchFeatures,
  type PlanFeatures,
} from '../services/api';
import { useT, type TFunction } from '../i18n';
import { useClienteAtivo } from '../contexts/ClienteAtivoContext';
import { useFormatters } from '../i18n/format';

/** Rotulos de tipo de documento (i18n-aware). */
function getDocTypeLabels(t: TFunction): Record<string, string> {
  return {
    nfe: t('common.doctype.nfe'),
    nfse: t('common.doctype.nfse'),
    nfcom: t('common.doctype.nfcom'),
    email: t('common.doctype.email'),
    zip: t('common.doctype.zip'),
    unknown: t('common.doctype.unknown'),
  };
}

const STATUS_STYLES: Record<string, string> = {
  recebido: 'bg-brand-100 text-brand-600',
  concluido: 'bg-green-100 text-green-700',
  revisao: 'bg-yellow-100 text-yellow-800',
  erro: 'bg-red-100 text-red-700',
  processado: 'bg-blue-100 text-blue-700',
};

// Colunas fiscais padronizadas para o grid estilo Excel (Issue #182).
function getFiscalColumns(t: TFunction): { key: string; label: string }[] {
  return [
    { key: 'numero_nota', label: t('documents.col.numero') },
    { key: 'serie', label: t('documents.col.serie') },
    { key: 'prestador_cnpj', label: t('documents.col.prestadorCnpj') },
    { key: 'prestador_nome', label: t('documents.col.prestador') },
    { key: 'tomador_cnpj', label: t('documents.col.tomadorCnpj') },
    { key: 'tomador_nome', label: t('documents.col.tomador') },
    { key: 'valor_total', label: t('documents.col.valorTotal') },
    { key: 'valor_servico', label: t('documents.col.valorServico') },
    { key: 'data_emissao', label: t('documents.col.dataEmissao') },
    { key: 'municipio', label: t('documents.col.municipio') },
    { key: 'codigo_verificacao', label: t('documents.col.codVerificacao') },
    { key: 'descricao_servico', label: t('documents.col.descricao') },
    { key: 'prestador_inscricao_municipal', label: t('documents.col.imPrestador') },
  ];
}

// Mapeia field names crus para i18n keys human-readable (reutiliza documents.col.*).
const FIELD_LABEL_MAP: Record<string, string> = {
  numero_nota: "documents.col.numero",
  serie: "documents.col.serie",
  prestador_cnpj: "documents.col.prestadorCnpj",
  prestador_nome: "documents.col.prestador",
  tomador_cnpj: "documents.col.tomadorCnpj",
  tomador_nome: "documents.col.tomador",
  valor_total: "documents.col.valorTotal",
  valor_servico: "documents.col.valorServico",
  data_emissao: "documents.col.dataEmissao",
  municipio: "documents.col.municipio",
  codigo_verificacao: "documents.col.codVerificacao",
  descricao_servico: "documents.col.descricao",
  prestador_inscricao_municipal: "documents.col.imPrestador",
  doc_type: "documents.col.docType",
};

/** Retorna label human-readable para um field name, com fallback para o nome cru.
 *  Labels adaptativos CNPJ por document_type: nfe/danfe usam Emitente/Destinatario. */
function getFieldLabel(
  name: string,
  t: (key: string) => string,
  documentType?: string,
): string {
  let i18nKey = FIELD_LABEL_MAP[name];
  // Labels adaptativos: nfe/danfe usam Emitente/Destinatario em vez de Prestador/Tomador.
  if (documentType === 'nfe' || documentType === 'danfe') {
    if (name === 'prestador_cnpj') i18nKey = 'documents.col.prestadorCnpjEmitente';
    else if (name === 'tomador_cnpj') i18nKey = 'documents.col.tomadorCnpjDestinatario';
  }
  if (i18nKey) {
    const translated = t(i18nKey);
    return translated !== i18nKey ? translated : name;
  }
  return name;
}

/** Cor de fundo da celula conforme confianca da extracao. */
function confidenceColor(conf: number): string {
  const pct = conf * 100;
  if (pct >= 85) return 'bg-green-50';
  if (pct >= 70) return 'bg-yellow-50';
  return 'bg-red-50';
}

/** Linha do grid: documento + campos extraidos (carregados sob demanda). */
interface GridRow {
  doc: DocumentSummary;
  fields: Record<string, ExtractedFieldData>;
  loaded: boolean;
  loading: boolean;
}

type SortDir = 'asc' | 'desc';

function Documents({ userRole, userId }: { userRole?: string; userId?: string }) {
  const t = useT();
  // Issue #329: cliente ativo do contador (separacao de telas).
  const { clienteAtivoId, clienteAtivo } = useClienteAtivo();
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // View mode: grid (planilha) ou cards (atual)
  // Issue #549: em telas < 768px o default e 'cards' — o grid estilo
  // planilha fica largo demais para mobile. Nao ha persistencia da
  // escolha em localStorage; se um dia houver, respeitar a escolha salva.
  const [viewMode, setViewMode] = useState<'grid' | 'cards'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'grid',
  );

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterImportId, setFilterImportId] = useState('');

  // Detail
  const [selectedDoc, setSelectedDoc] = useState<DocumentDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Grid state: campos por documento, ordenacao e filtros por coluna
  const [rows, setRows] = useState<GridRow[]>([]);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [exporting, setExporting] = useState(false);
  // Issue #248: features do plano para gating do botao Exportar CSV.
  const [features, setFeatures] = useState<PlanFeatures | null>(null);
  // Issue #193: modal de provenance.
  const [provenanceModal, setProvenanceModal] = useState<Provenance | null>(null);

  // Trash / delete feature
  const [showTrash, setShowTrash] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DocumentSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const isAdmin = userRole === 'admin' || userRole === 'super_user';

  // Issue #417: se o usuario deixar de ser admin, fecha a visao de lixeira.
  useEffect(() => {
    if (!isAdmin && showTrash) {
      setShowTrash(false);
    }
  }, [isAdmin, showTrash]);

  // Batch selection (Feature 2)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);

  // ID of the document currently open in the detail panel (for row highlight)
  const selectedDocId = selectedDoc?.document.id ?? null;

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listDocuments({
        doc_type: filterType || undefined,
        status: showTrash ? 'trashed' : (filterStatus || undefined),
        import_id: filterImportId || undefined,
        date_from: filterDateFrom || undefined,
        date_to: filterDateTo || undefined,
        limit: 200,
        // Issue #329: filtra por cliente ativo do contador.
        cliente_id: clienteAtivoId || undefined,
      });
      setDocs(res.documents);
      setRows(res.documents.map((doc) => ({ doc, fields: {}, loaded: false, loading: false })));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('documents.error.load'));
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, filterDateFrom, filterDateTo, filterImportId, showTrash, clienteAtivoId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  // Issue #248: busca features do plano para gating do botao Exportar CSV.
  useEffect(() => {
    fetchFeatures()
      .then((resp) => setFeatures(resp.features))
      .catch(() => setFeatures(null));
  }, []);

  const openDetail = useCallback(async (docId: string) => {
    setDetailLoading(true);
    try {
      const detail = await getDocument(docId);
      setSelectedDoc(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('documents.error.loadDetail'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  /** Carrega os campos de um documento sob demanda (lazy) para o grid. */
  const loadRowFields = useCallback(async (docId: string) => {
    setRows((prev) =>
      prev.map((r) => (r.doc.id === docId ? { ...r, loading: true } : r)),
    );
    try {
      const detail = await getDocument(docId);
      const fields = detail.pipeline_result?.root?.fields ?? {};
      setRows((prev) =>
        prev.map((r) =>
          r.doc.id === docId ? { ...r, fields, loaded: true, loading: false } : r,
        ),
      );
    } catch {
      // FIX #215: Setar loaded:true para parar loop infinito do useEffect
      setRows((prev) =>
        prev.map((r) => (r.doc.id === docId ? { ...r, loaded: true, loading: false } : r)),
      );
    }
  }, []);

  /** Carrega os campos de todas as linhas visiveis (para o grid). */
  const loadAllFields = useCallback(async () => {
    const pending = rows.filter((r) => !r.loaded && !r.loading);
    await Promise.all(pending.map((r) => loadRowFields(r.doc.id)));
  }, [rows, loadRowFields]);

  // Carrega campos automaticamente quando entra no modo grid
  useEffect(() => {
    if (viewMode === 'grid' && rows.some((r) => !r.loaded && !r.loading)) {
      loadAllFields();
    }
  }, [viewMode, rows, loadAllFields]);

  /** Salva edicao inline de uma celula via PUT /api/documents/{id}/fields. */
  const saveCell = useCallback(
    async (docId: string, field: string, value: string) => {
      try {
        await updateDocumentFields(docId, { [field]: value });
        // Atualiza localmente
        setRows((prev) =>
          prev.map((r) =>
            r.doc.id === docId
              ? {
                  ...r,
                  fields: {
                    ...r.fields,
                    [field]: {
                      ...(r.fields[field] || {}),
                      value,
                      confidence: 1.0,
                      source: 'manual_correction',
                      corrected: true,
                    },
                  },
                }
              : r,
          ),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : t('documents.error.saveField'));
      }
    },
    [],
  );

  /** Exporta a lista em CSV ou XLSX. */
  const handleExport = useCallback(
    async (format: 'csv' | 'xlsx') => {
      setExporting(true);
      try {
        await exportDocuments(format, {
          doc_type: filterType || undefined,
          status: filterStatus || undefined,
          import_id: filterImportId || undefined,
          date_from: filterDateFrom || undefined,
          date_to: filterDateTo || undefined,
          // Issue #329: exporta apenas do cliente ativo.
          cliente_id: clienteAtivoId || undefined,
          // Exporta apenas os documentos selecionados quando houver selecao.
          ids: selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('documents.error.export'));
      } finally {
        setExporting(false);
      }
    },
    [filterType, filterStatus, filterDateFrom, filterDateTo, filterImportId, clienteAtivoId, selectedIds],
  );

  /** Deleta (soft-delete) um documento, movendo para a lixeira. */
  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await deleteDocument(deleteConfirm.id);
      setDocs((prev) => prev.filter((d) => d.id !== deleteConfirm.id));
      setRows((prev) => prev.filter((r) => r.doc.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('documents.error.delete'));
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm, t]);

  /** Restaura um documento da lixeira. */
  const handleRestore = useCallback(async (docId: string) => {
    setRestoring(docId);
    try {
      await restoreDocument(docId);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
      setRows((prev) => prev.filter((r) => r.doc.id !== docId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('documents.error.restore'));
    } finally {
      setRestoring(null);
    }
  }, [t]);

  // Filtragem e ordenacao do grid
  const sortedRows = useMemo(() => {
    let result = rows;
    // Filtros por coluna
    const activeFilters = Object.entries(colFilters).filter(([, v]) => v.trim());
    if (activeFilters.length > 0) {
      result = result.filter((r) =>
        activeFilters.every(([col, val]) => {
          if (col === 'import_id') {
            const cellVal = r.doc.import_id ?? '';
            return cellVal.toLowerCase().includes(val.toLowerCase());
          }
          const field = r.fields[col];
          const cellVal = field ? String(field.value ?? '') : '';
          return cellVal.toLowerCase().includes(val.toLowerCase());
        }),
      );
    }
    // Ordenacao
    if (sortCol) {
      result = [...result].sort((a, b) => {
        if (sortCol === 'import_id') {
          const aStr = a.doc.import_id ?? '';
          const bStr = b.doc.import_id ?? '';
          const cmp = aStr.localeCompare(bStr, 'pt-BR', { numeric: true });
          return sortDir === 'asc' ? cmp : -cmp;
        }
        const av = a.fields[sortCol]?.value;
        const bv = b.fields[sortCol]?.value;
        const aStr = av === null || av === undefined ? '' : String(av);
        const bStr = bv === null || bv === undefined ? '' : String(bv);
        const cmp = aStr.localeCompare(bStr, 'pt-BR', { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [rows, colFilters, sortCol, sortDir]);

  const toggleSort = useCallback((col: string) => {
    setSortCol((prev) => {
      if (prev !== col) {
        setSortDir('asc');
        return col;
      }
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return prev;
    });
  }, []);

  // --- Batch selection handlers (Feature 2) ---

  /** Toggle a single document's selection in the batch set. */
  const handleToggleSelect = useCallback((docId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  }, []);

  /** Toggle select-all for the currently visible (sorted) rows. */
  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const visibleIds = sortedRows.map((r) => r.doc.id);
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [sortedRows]);

  /** Clear all batch selections. */
  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setBatchError(null);
  }, []);

  /** Batch delete: call deleteDocument for each selected ID, collect failures. */
  const handleBatchDelete = useCallback(async () => {
    setBatchDeleting(true);
    setBatchError(null);
    const ids = Array.from(selectedIds);
    const failed: string[] = [];
    for (const id of ids) {
      try {
        await deleteDocument(id);
      } catch {
        failed.push(id);
      }
    }
    // Remove successfully deleted docs from local state
    const failedSet = new Set(failed);
    const succeeded = ids.filter((id) => !failedSet.has(id));
    if (succeeded.length > 0) {
      setDocs((prev) => prev.filter((d) => !succeeded.includes(d.id)));
      setRows((prev) => prev.filter((r) => !succeeded.includes(r.doc.id)));
    }
    if (failed.length > 0) {
      setBatchError(t('documents.batch.deletePartialError', { failed: failed.length, total: ids.length }));
      // Keep only the failed IDs selected so the user can retry
      setSelectedIds(new Set(failed));
    } else {
      setSelectedIds(new Set());
    }
    setBatchDeleteConfirm(false);
    setBatchDeleting(false);
    // If the selected detail doc was deleted, close the panel
    if (selectedDocId && succeeded.includes(selectedDocId)) {
      setSelectedDoc(null);
    }
  }, [selectedIds, selectedDocId, t]);

  // Whether all visible rows are currently selected
  const allVisibleSelected = sortedRows.length > 0 && sortedRows.every((r) => selectedIds.has(r.doc.id));
  const someVisibleSelected = sortedRows.some((r) => selectedIds.has(r.doc.id));

  return (
    <div className="space-y-6">
      {/* Issue #329: aviso do cliente ativo do contador.
          Issue #549: strings migradas para i18n (ambos os banners). */}
      {clienteAtivo ? (
        <div className="rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm text-blue-800">
          {t('documents.clientBanner.active', { name: clienteAtivo.nome })}
        </div>
      ) : (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          {t('documents.clientBanner.all')}
        </div>
      )}
      <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-brand-900">{t('documents.title')}</h1>
          <div className="flex items-center gap-2">
            {/* Trash toggle — Issue #417: apenas admin/owner/super_user */}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowTrash((prev) => !prev)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  showTrash
                    ? 'bg-red-600 text-white'
                    : 'border border-brand-300 bg-white text-brand-700 hover:bg-brand-50'
                }`}
              >
                {t('documents.trash')}
              </button>
            )}
            {/* Toggle view mode */}
            <div className="inline-flex overflow-hidden rounded-md border border-brand-300">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1.5 text-sm font-medium ${
                  viewMode === 'grid'
                    ? 'bg-brand-700 text-white'
                    : 'bg-white text-brand-700 hover:bg-brand-50'
                }`}
              >
                {t('documents.view.grid')}
              </button>
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 text-sm font-medium ${
                  viewMode === 'cards'
                    ? 'bg-brand-700 text-white'
                    : 'bg-white text-brand-700 hover:bg-brand-50'
                }`}
              >
                {t('documents.view.cards')}
              </button>
            </div>
            {/* Export buttons */}
            {/* Issue #248: Exportar CSV exige plano Pro (export_csv=true). */}
            {(!features || features.export_csv) && (
              <button
                type="button"
                onClick={() => handleExport('csv')}
                disabled={exporting || loading}
                className="rounded-md border border-brand-300 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
              >
                {t('documents.export.csv')}
              </button>
            )}
            <button
              type="button"
              onClick={() => handleExport('xlsx')}
              disabled={exporting || loading}
              className="rounded-md border border-brand-300 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
            >
              {t('documents.export.excel')}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs text-brand-500">{t('common.type')}</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="rounded-md border border-brand-300 px-2 py-1.5 text-sm text-brand-800"
            >
              <option value="">{t('common.all')}</option>
              <option value="nfe">{t('common.doctype.nfe')}</option>
              <option value="nfse">{t('common.doctype.nfse')}</option>
              <option value="nfcom">{t('common.doctype.nfcom')}</option>
              <option value="email">{t('common.doctype.email')}</option>
              <option value="zip">{t('common.doctype.zip')}</option>
              <option value="unknown">{t('common.doctype.unknown')}</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">{t('common.status')}</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-md border border-brand-300 px-2 py-1.5 text-sm text-brand-800"
            >
              <option value="">{t('common.all')}</option>
              <option value="concluido">{t('documents.status.concluido')}</option>
              <option value="revisao">{t('documents.status.revisao')}</option>
              <option value="erro">{t('documents.status.erro')}</option>
              <option value="recebido">{t('documents.status.recebido')}</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">{t('common.from')}</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="rounded-md border border-brand-300 px-2 py-1.5 text-sm text-brand-800"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">{t('common.to')}</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="rounded-md border border-brand-300 px-2 py-1.5 text-sm text-brand-800"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">{t('common.importId')}</label>
            <input
              type="text"
              placeholder={t('documents.filter.importIdPlaceholder')}
              value={filterImportId}
              onChange={(e) => setFilterImportId(e.target.value)}
              className="w-40 rounded-md border border-brand-300 px-2 py-1.5 text-sm text-brand-800"
            />
          </div>
          <button
            type="button"
            onClick={fetchDocs}
            className="rounded-md bg-brand-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-800"
          >
            {t('common.filter')}
          </button>
        </div>

        {error && (
          <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        {/* Batch action bar (Feature 2) */}
        {selectedIds.size > 0 && !showTrash && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-brand-300 bg-brand-50 px-4 py-3">
            <span className="text-sm font-medium text-brand-800">
              {t('documents.batch.selectedCount', { count: selectedIds.size })}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBatchDeleteConfirm(true)}
                disabled={batchDeleting}
                className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {t('documents.delete')}
              </button>
              <button
                type="button"
                onClick={handleClearSelection}
                disabled={batchDeleting}
                className="rounded-md border border-brand-300 bg-white px-4 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Batch error message */}
        {batchError && (
          <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{batchError}</p>
        )}

        {loading ? (
          <p className="text-sm text-brand-600">{t('common.loading')}</p>
        ) : docs.length === 0 ? (
          <p className="text-sm text-brand-500">{t('documents.empty')}</p>
        ) : showTrash ? (
          <TrashView
            docs={docs}
            onRestore={handleRestore}
            restoring={restoring}
            formatDateTime={useFormatters().formatDateTime}
          />
        ) : viewMode === 'cards' ? (
          <CardsView
            docs={docs}
            onOpen={openDetail}
            onDelete={setDeleteConfirm}
            isAdmin={isAdmin}
            userId={userId}
            selectedDocId={selectedDocId}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            allSelected={allVisibleSelected}
            someSelected={someVisibleSelected}
          />
        ) : (
          <GridView
            rows={sortedRows}
            sortCol={sortCol}
            sortDir={sortDir}
            onSort={toggleSort}
            colFilters={colFilters}
            onColFilter={(col, val) =>
              setColFilters((prev) => ({ ...prev, [col]: val }))
            }
            onOpen={openDetail}
            onSaveCell={saveCell}
            onProvenance={setProvenanceModal}
            onDelete={setDeleteConfirm}
            isAdmin={isAdmin}
            userId={userId}
            selectedDocId={selectedDocId}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            allSelected={allVisibleSelected}
            someSelected={someVisibleSelected}
          />
        )}
      </div>

      {/* Detail panel */}
      {detailLoading && (
        <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-brand-600">{t('documents.loadingDetails')}</p>
        </div>
      )}

      {selectedDoc && !detailLoading && (
        <DocumentDetail
          detail={selectedDoc}
          onClose={() => setSelectedDoc(null)}
          onDownload={(format) =>
            downloadDocument(
              selectedDoc.document.id,
              format,
              `${selectedDoc.document.filename}.${format === 'fields' ? 'csv' : format}`,
            )
          }
          onProvenance={setProvenanceModal}
          onDelete={(doc) => { setSelectedDoc(null); setDeleteConfirm(doc); }}
          isAdmin={isAdmin}
          userId={userId}
        />
      )}

      {/* Issue #193: modal de provenance */}
      {provenanceModal && (
        <ProvenanceModal
          provenance={provenanceModal}
          onClose={() => setProvenanceModal(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-brand-900">{t('documents.delete')}</h2>
            </div>
            <p className="mb-2 text-sm text-brand-700">
              <span className="font-medium">{deleteConfirm.filename}</span>
            </p>
            <p className="mb-6 text-sm text-brand-600">
              {t('documents.deleteConfirm')}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? t('common.processing') : t('documents.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch delete confirmation modal (Feature 2) */}
      {batchDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-brand-900">{t('documents.batch.deleteTitle')}</h2>
            </div>
            <p className="mb-6 text-sm text-brand-600">
              {t('documents.batch.deleteConfirm', { count: selectedIds.size })}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setBatchDeleteConfirm(false)}
                disabled={batchDeleting}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleBatchDelete}
                disabled={batchDeleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {batchDeleting ? t('common.processing') : t('documents.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------


/** Resumo legivel da cadeia de origem (issue #193). */
function provenanceSummary(prov: Provenance | null | undefined): string {
  if (!prov) return '';
  const chain = prov.container_chain || [];
  if (chain.length === 0) return prov.file_type || prov.extraction_method || '';
  return chain.map((c) => c.container_type.toUpperCase()).join(' → ');
}

function ProvenanceModal({
  provenance,
  onClose,
}: {
  provenance: Provenance | null;
  onClose: () => void;
}) {
  const t = useT();
  if (!provenance) return null;
  const chain = provenance.container_chain || [];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-brand-800">{t('documents.provenance.title')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-brand-500 hover:bg-brand-50"
          >
            {t('documents.provenance.close')}
          </button>
        </div>
        {provenance.source_path && (
          <div className="mb-4">
            <span className="text-xs text-brand-500">source_path</span>
            <p className="break-all font-mono text-sm text-brand-800">
              {provenance.source_path}
            </p>
          </div>
        )}
        {chain.length > 0 ? (
          <div className="mb-4">
            <span className="text-xs text-brand-500">container_chain</span>
            <ol className="mt-1 space-y-2">
              {chain.map((c, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 rounded bg-brand-50 px-3 py-2 text-sm"
                >
                  <span className="rounded bg-brand-200 px-2 py-0.5 text-xs font-semibold text-brand-700">
                    {c.container_type}
                  </span>
                  <span className="font-medium text-brand-800">{c.container_name}</span>
                  {c.attachment_name && (
                    <span className="text-brand-500">→ {c.attachment_name}</span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <p className="mb-4 text-sm text-brand-500">
            {t('documents.provenance.noChain')}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {provenance.file_type && (
            <div>
              <span className="text-xs text-brand-500">file_type</span>
              <p className="font-medium text-brand-800">{provenance.file_type}</p>
            </div>
          )}
          {provenance.file_name && (
            <div>
              <span className="text-xs text-brand-500">file_name</span>
              <p className="font-medium text-brand-800">{provenance.file_name}</p>
            </div>
          )}
          {provenance.extraction_method && (
            <div>
              <span className="text-xs text-brand-500">extraction_method</span>
              <p className="font-medium text-brand-800">{provenance.extraction_method}</p>
            </div>
          )}
          {provenance.template && (
            <div>
              <span className="text-xs text-brand-500">template</span>
              <p className="font-medium text-brand-800">{provenance.template}</p>
            </div>
          )}
          {provenance.inherited_from && (
            <div className="col-span-2">
              <span className="text-xs text-brand-500">inherited_from</span>
              <p className="font-medium text-brand-800">
                {provenance.inherited_from.child_source_type}
                {provenance.inherited_from.child_file_name
                  ? ` (${provenance.inherited_from.child_file_name})`
                  : ''}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// GridView — grid estilo Excel/planilha com celulas editaveis
// ---------------------------------------------------------------------------

function GridView({
  rows,
  sortCol,
  sortDir,
  onSort,
  colFilters,
  onColFilter,
  onOpen,
  onSaveCell,
  onProvenance,
  onDelete,
  isAdmin,
  userId,
  selectedDocId,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  someSelected,
}: {
  rows: GridRow[];
  sortCol: string | null;
  sortDir: SortDir;
  onSort: (col: string) => void;
  colFilters: Record<string, string>;
  onColFilter: (col: string, val: string) => void;
  onOpen: (docId: string) => void;
  onSaveCell: (docId: string, field: string, value: string) => void;
  onProvenance: (prov: Provenance | null) => void;
  onDelete: (doc: DocumentSummary) => void;
  isAdmin: boolean;
  userId?: string;
  selectedDocId: string | null;
  selectedIds: Set<string>;
  onToggleSelect: (docId: string) => void;
  onToggleSelectAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
}) {
  const t = useT();
  const fiscalColumns = getFiscalColumns(t);
  const gridColumnIds = useMemo(
    () => ['select', 'importId', ...fiscalColumns.map((c) => c.key), 'origin', 'actions'],
    [fiscalColumns],
  );
  const { widths, handleResize } = useColumnWidths(GRID_WIDTHS_KEY, gridColumnIds, GRID_DEFAULT_WIDTHS);
  return (
    <div className="overflow-auto border border-brand-200" style={{ maxHeight: '70vh' }}>
      <table className="border-collapse text-sm" style={{ tableLayout: 'fixed', width: `${gridColumnIds.reduce((sum, id) => sum + (widths[id] ?? 150), 0)}px` }}>
        <colgroup>
          {gridColumnIds.map((id) => (
            <col key={id} style={{ width: widths[id] ? `${widths[id]}px` : undefined }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-20">
          {/* Linha de filtros */}
          <tr className="bg-brand-50">
            <th className="bg-brand-50 px-1 py-1 w-10">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                onChange={onToggleSelectAll}
                className="h-4 w-4 cursor-pointer rounded border-brand-300 text-brand-600 focus:ring-brand-500"
                title={t('documents.batch.selectAll')}
              />
            </th>
            <th className="sticky left-0 z-20 bg-brand-50 px-2 py-1">
              <input
                type="text"
                placeholder={t('common.filterPlaceholder')}
                value={colFilters['import_id'] || ''}
                onChange={(e) => onColFilter('import_id', e.target.value)}
                className="w-28 rounded border border-brand-200 px-1 py-0.5 text-xs"
              />
            </th>
            {fiscalColumns.map((col) => (
              <th key={`f-${col.key}`} className="px-2 py-1">
                <input
                  type="text"
                  placeholder={t('common.filterPlaceholder')}
                  value={colFilters[col.key] || ''}
                  onChange={(e) => onColFilter(col.key, e.target.value)}
                  className="w-full min-w-[80px] rounded border border-brand-200 px-1 py-0.5 text-xs"
                />
              </th>
            ))}
          </tr>
          {/* Linha de headers */}
          <tr className="bg-brand-100 text-brand-800">
            <ResizableHeader
              columnId="select"
              width={widths['select']}
              onResize={handleResize}
              className="whitespace-nowrap border-b border-brand-200 bg-brand-100 px-1 py-2 w-10"
            />
            <ResizableHeader
              columnId="importId"
              width={widths['importId']}
              onResize={handleResize}
              className="sticky left-0 z-20 cursor-pointer select-none whitespace-nowrap border-b border-brand-200 bg-brand-100 px-3 py-2 text-left font-semibold hover:bg-brand-200"
            >
              <span className="inline-flex items-center gap-1" onClick={() => onSort('import_id')}>
                {t('common.importId')}
                {sortCol === 'import_id' && (
                  <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>
                )}
              </span>
            </ResizableHeader>
            {fiscalColumns.map((col) => (
              <ResizableHeader
                key={col.key}
                columnId={col.key}
                width={widths[col.key]}
                onResize={handleResize}
                className="cursor-pointer select-none whitespace-nowrap border-b border-brand-200 px-3 py-2 text-left font-semibold hover:bg-brand-200"
              >
                <span className="inline-flex items-center gap-1" onClick={() => onSort(col.key)}>
                  {col.label}
                  {sortCol === col.key && (
                    <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </span>
              </ResizableHeader>
            ))}
            <ResizableHeader
              columnId="origin"
              width={widths['origin']}
              onResize={handleResize}
              className="whitespace-nowrap border-b border-brand-200 px-3 py-2 text-left font-semibold"
            >
              {t('common.origin')}
            </ResizableHeader>
            <ResizableHeader
              columnId="actions"
              width={widths['actions']}
              onResize={handleResize}
              className="whitespace-nowrap border-b border-brand-200 px-3 py-2 text-left font-semibold"
            >
              {t('documents.actions')}
            </ResizableHeader>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isSelected = selectedIds.has(row.doc.id);
            const isOpen = selectedDocId === row.doc.id;
            return (
            <tr
              key={row.doc.id}
              className={`border-b border-brand-100 hover:bg-brand-50/40 ${
                isOpen ? 'bg-brand-50' : isSelected ? 'bg-brand-50/70' : ''
              }`}
            >
              <td className="whitespace-nowrap border-r border-brand-100 px-1 py-1.5 w-10 text-center">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => { e.stopPropagation(); onToggleSelect(row.doc.id); }}
                  className="h-4 w-4 cursor-pointer rounded border-brand-300 text-brand-600 focus:ring-brand-500"
                />
                {(isAdmin || row.doc.uploaded_by === userId) && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(row.doc); }}
                    className="text-red-500 hover:text-red-700"
                    title={t('documents.delete')}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                  </button>
                )}
              </td>
              <td
                onClick={() => onOpen(row.doc.id)}
                className={`sticky left-0 z-10 cursor-pointer whitespace-nowrap border-r border-brand-100 px-3 py-1.5 font-mono text-xs hover:bg-brand-50 ${isOpen ? 'bg-brand-200 font-semibold text-brand-900' : 'bg-white text-brand-600'}`}
              >
                {row.doc.import_id || '—'}
              </td>
              {fiscalColumns.map((col) => (
                <EditableCell
                  key={col.key}
                  field={col.key}
                  data={row.fields[col.key]}
                  loaded={row.loaded}
                  loading={row.loading}
                  sticky={false}
                  onSave={(value) => onSaveCell(row.doc.id, col.key, value)}
                  onOpen={() => onOpen(row.doc.id)}
                  canEdit={isAdmin || row.doc.uploaded_by === userId}
                />
              ))}
              <td className="whitespace-nowrap border-r border-brand-100 px-3 py-1.5">
                {row.doc.provenance ? (
                  <button
                    type="button"
                    onClick={() => onProvenance(row.doc.provenance)}
                    className="cursor-pointer text-xs text-brand-600 underline hover:text-brand-800"
                    title={row.doc.source_path || ''}
                  >
                    {provenanceSummary(row.doc.provenance)}
                  </button>
                ) : (
                  <span className="text-xs text-brand-400">—</span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 text-center">
                {(isAdmin || row.doc.uploaded_by === userId) && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(row.doc); }}
                    className="text-red-500 hover:text-red-700"
                    title={t('documents.delete')}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                  </button>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EditableCell({
  field,
  data,
  loaded,
  loading,
  sticky,
  onSave,
  onOpen,
  canEdit,
}: {
  field: string;
  data: ExtractedFieldData | undefined;
  loaded: boolean;
  loading: boolean;
  sticky: boolean;
  onSave: (value: string) => void;
  onOpen: () => void;
  canEdit: boolean;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const value = data?.value;
  const conf = data?.confidence ?? 0;
  const corrected = data?.corrected;
  const display = value === null || value === undefined ? '' : String(value);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = useCallback(() => {
    setDraft(display);
    setEditing(true);
  }, [display]);

  const commit = useCallback(() => {
    setEditing(false);
    if (draft !== display) {
      onSave(draft);
    }
  }, [draft, display, onSave]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  if (editing) {
    return (
      <td
        className={`whitespace-nowrap border-r border-brand-100 px-1 py-1 ${sticky ? 'sticky left-0 z-10 bg-white' : ''}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') cancel();
          }}
          className="w-full min-w-[80px] rounded border border-brand-400 px-1 py-0.5 text-sm"
        />
      </td>
    );
  }

  // Celula da primeira coluna (numero_nota) e sticky e clicavel p/ abrir detalhe
  return (
    <td
      onDoubleClick={canEdit ? startEdit : undefined}
      onClick={sticky ? onOpen : undefined}
      title={loaded ? t('documents.cell.tooltip', { field, conf: Math.round(conf * 100) }) : undefined}
      className={`whitespace-nowrap border-r border-brand-100 px-3 py-1.5 text-brand-800 ${
        sticky ? 'sticky left-0 z-10 cursor-pointer font-medium' : ''
      } ${loaded ? confidenceColor(conf) : 'bg-white'} ${corrected ? 'ring-1 ring-inset ring-blue-300' : ''}`}
    >
      {loading ? (
        <span className="text-xs text-brand-400">...</span>
      ) : loaded ? (
        <span className="inline-flex items-center gap-1">
          <span>{display || '—'}</span>
          {corrected && (
            <span className="rounded bg-blue-100 px-1 text-[10px] font-medium text-blue-700">
              {t('common.corrected')}
            </span>
          )}
        </span>
      ) : (
        <span className="text-xs text-brand-400">—</span>
      )}
    </td>
  );
}

// ---------------------------------------------------------------------------
// CardsView — tabela simples (view cards atual) com colunas redimensionaveis
// ---------------------------------------------------------------------------

const CARDS_COLUMN_IDS = [
  'select', 'importId', 'file', 'type', 'status', 'fields',
  'confidence', 'children', 'date', 'processedAt', 'actions',
] as const;
const CARDS_WIDTHS_KEY = 'auranota_doc_column_widths';
const GRID_WIDTHS_KEY = 'auranota_doc_grid_column_widths';
const CARDS_MIN_WIDTH = 80;
const DEFAULT_COL_WIDTH = 150;

// Larguras padr??o diferenciadas por coluna do grid de documentos.
// Colunas sem entrada aqui usam DEFAULT_COL_WIDTH como fallback.
const GRID_DEFAULT_WIDTHS: Record<string, number> = {
  select: 40,
  importId: 90,
  numero_nota: 120,
  serie: 70,
  prestador_cnpj: 140,
  prestador_nome: 220,
  tomador_cnpj: 140,
  tomador_nome: 220,
  valor_total: 120,
  valor_servico: 120,
  data_emissao: 120,
  municipio: 160,
  codigo_verificacao: 180,
  descricao_servico: 300,
  prestador_inscricao_municipal: 150,
  doc_type: 100,
  origin: 90,
  actions: 80,
};

/** Hook que gerencia larguras de colunas persistidas em localStorage. */
function useColumnWidths(storageKey: string, ids: readonly string[], defaultWidths?: Record<string, number>) {
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    const result: Record<string, number> = {};
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as Record<string, number>;
        for (const id of ids) {
          if (typeof parsed[id] === 'number' && parsed[id] >= CARDS_MIN_WIDTH) {
            result[id] = parsed[id];
          } else {
            result[id] = defaultWidths?.[id] ?? DEFAULT_COL_WIDTH;
          }
        }
        return result;
      }
    } catch {
      /* ignore */
    }
    // Initialize ALL columns with default width so that tableLayout:fixed
    // works correctly and the browser re-layouts when widths change.
    for (const id of ids) {
      result[id] = defaultWidths?.[id] ?? DEFAULT_COL_WIDTH;
    }
    return result;
  });

  const handleResize = useCallback((columnId: string, newWidth: number) => {
    setWidths((prev) => {
      const clamped = Math.max(CARDS_MIN_WIDTH, Math.round(newWidth));
      const updated = { ...prev, [columnId]: clamped };
      try {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch {
        /* ignore quota */
      }
      return updated;
    });
  }, [storageKey]);

  return { widths, handleResize };
}

/** Header de coluna com drag handle para redimensionamento. */
function ResizableHeader({
  columnId,
  width,
  onResize,
  className,
  children,
  style,
}: {
  columnId: string;
  width?: number;
  onResize: (columnId: string, newWidth: number) => void;
  className?: string;
  children?: ReactNode;
  style?: CSSProperties;
}) {
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const newWidth = startWidth.current + (e.clientX - startX.current);
    onResize(columnId, newWidth);
  }, [columnId, onResize]);

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e: ReactMouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    startX.current = e.clientX;
    // Use actual rendered width from the DOM, not the React prop (which may be undefined).
    // This prevents the column from jumping when dragging starts on columns without
    // an explicit width set (e.g., first visit with no localStorage data).
    const thEl = (e.currentTarget as HTMLElement).parentElement;
    startWidth.current = thEl ? thEl.getBoundingClientRect().width : (width ?? CARDS_MIN_WIDTH);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <th
      className={`relative ${className ?? ''}`}
      style={{ width: width ? `${width}px` : undefined, ...style }}
    >
      {children}
      <div
        onMouseDown={handleMouseDown}
        className="absolute right-0 top-0 z-30 h-full w-1.5 cursor-col-resize select-none pointer-events-auto touch-none bg-transparent transition-colors hover:bg-brand-500/70 active:bg-brand-600"
        title="Arraste para redimensionar"
      />
    </th>
  );
}

function CardsView({
  docs,
  onOpen,
  onDelete,
  isAdmin,
  userId,
  selectedDocId,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  someSelected,
}: {
  docs: DocumentSummary[];
  onOpen: (docId: string) => void;
  onDelete: (doc: DocumentSummary) => void;
  isAdmin: boolean;
  userId?: string;
  selectedDocId: string | null;
  selectedIds: Set<string>;
  onToggleSelect: (docId: string) => void;
  onToggleSelectAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
}) {
  const t = useT();
  const { formatDateTime: fmtDateTime, formatConfidence: fmtConfidence } = useFormatters();
  const docTypeLabels = getDocTypeLabels(t);
  const { widths, handleResize } = useColumnWidths(CARDS_WIDTHS_KEY, CARDS_COLUMN_IDS);
  return (
    <div className="overflow-x-auto">
      <table className="text-left text-sm" style={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {CARDS_COLUMN_IDS.map((id) => (
            <col key={id} style={{ width: widths[id] ? `${widths[id]}px` : undefined }} />
          ))}
        </colgroup>
        <thead className="border-b border-brand-200 text-brand-700">
          <tr>
            <ResizableHeader
              columnId="select"
              width={widths['select']}
              onResize={handleResize}
              className="py-2 pr-4 w-10 text-center"
            >
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                onChange={onToggleSelectAll}
                className="h-4 w-4 cursor-pointer rounded border-brand-300 text-brand-600 focus:ring-brand-500"
                title={t('documents.batch.selectAll')}
              />
            </ResizableHeader>
            <ResizableHeader columnId="importId" width={widths['importId']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.cards.importId')}
            </ResizableHeader>
            <ResizableHeader columnId="file" width={widths['file']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.cards.file')}
            </ResizableHeader>
            <ResizableHeader columnId="type" width={widths['type']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.cards.type')}
            </ResizableHeader>
            <ResizableHeader columnId="status" width={widths['status']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.cards.status')}
            </ResizableHeader>
            <ResizableHeader columnId="fields" width={widths['fields']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.cards.fields')}
            </ResizableHeader>
            <ResizableHeader columnId="confidence" width={widths['confidence']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.cards.confidence')}
            </ResizableHeader>
            <ResizableHeader columnId="children" width={widths['children']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.cards.children')}
            </ResizableHeader>
            <ResizableHeader columnId="date" width={widths['date']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.cards.date')}
            </ResizableHeader>
            <ResizableHeader columnId="processedAt" width={widths['processedAt']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.cards.processedAt')}
            </ResizableHeader>
            <ResizableHeader columnId="actions" width={widths['actions']} onResize={handleResize} className="py-2 pr-4 whitespace-nowrap">
              {t('documents.actions')}
            </ResizableHeader>
          </tr>
        </thead>
        <tbody>
          {docs.map((doc) => {
            const isSelected = selectedIds.has(doc.id);
            const isOpen = selectedDocId === doc.id;
            return (
            <tr
              key={doc.id}
              onClick={() => onOpen(doc.id)}
              className={`cursor-pointer border-b border-brand-100 hover:bg-brand-50 ${
                isOpen ? 'bg-brand-50' : isSelected ? 'bg-brand-50/70' : ''
              }`}
            >
              <td className="py-2 pr-4 w-10 text-center" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleSelect(doc.id)}
                  className="h-4 w-4 cursor-pointer rounded border-brand-300 text-brand-600 focus:ring-brand-500"
                />
                {(isAdmin || doc.uploaded_by === userId) && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(doc); }}
                    className="text-red-500 hover:text-red-700"
                    title={t('documents.delete')}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                  </button>
                )}
              </td>
              <td className={`py-2 pr-4 font-mono text-xs ${isOpen ? 'bg-brand-200 font-semibold text-brand-900' : 'text-brand-600'}`}>{doc.import_id || '—'}</td>
              <td className="py-2 pr-4 font-medium text-brand-800">{doc.filename}</td>
              <td className="py-2 pr-4 text-brand-600">
                {docTypeLabels[doc.doc_type] || doc.doc_type}
              </td>
              <td className="py-2 pr-4">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    STATUS_STYLES[doc.status] || 'bg-brand-100 text-brand-600'
                  }`}
                >
                  {doc.status}
                </span>
              </td>
              <td className="py-2 pr-4 text-brand-600">{doc.fields_count}</td>
              <td className="py-2 pr-4 text-brand-600">
                {doc.avg_confidence > 0 ? fmtConfidence(doc.avg_confidence) : '—'}
              </td>
              <td className="py-2 pr-4 text-brand-600">
                {doc.children_count > 0 ? doc.children_count : '—'}
              </td>
              <td className="py-2 pr-4 text-xs text-brand-500">{fmtDateTime(doc.created_at)}</td>
              <td className="py-2 pr-4 text-xs text-brand-500">{fmtDateTime(doc.processed_at)}</td>
              <td className="py-2 pr-4">
                {(isAdmin || doc.uploaded_by === userId) && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(doc); }}
                    className="text-red-500 hover:text-red-700"
                    title={t('documents.delete')}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                  </button>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TrashView — visualizacao de documentos na lixeira
// ---------------------------------------------------------------------------

function TrashView({
  docs,
  onRestore,
  restoring,
  formatDateTime,
}: {
  docs: DocumentSummary[];
  onRestore: (docId: string) => void;
  restoring: string | null;
  formatDateTime: (date: string | null) => string;
}) {
  const t = useT();
  const docTypeLabels = getDocTypeLabels(t);

  /** Calcula dias restantes ate a exclusao permanente (30 - dias desde deletado). */
  function daysUntilPurge(deletedAt: string | null): number {
    if (!deletedAt) return 30;
    const deleted = new Date(deletedAt);
    const now = new Date();
    const diffMs = now.getTime() - deleted.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - diffDays);
  }

  if (docs.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-brand-500">{t('documents.trashEmpty')}</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-brand-200 text-brand-700">
          <tr>
            <th className="py-2 pr-4">{t('documents.cards.file')}</th>
            <th className="py-2 pr-4">{t('documents.cards.type')}</th>
            <th className="py-2 pr-4">{t('documents.trashDeletedAt')}</th>
            <th className="py-2 pr-4">{t('documents.daysUntilPurge')}</th>
            <th className="py-2 pr-4">{t('documents.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((doc) => {
            const daysLeft = daysUntilPurge(doc.deleted_at);
            return (
              <tr key={doc.id} className="border-b border-brand-100">
                <td className="py-2 pr-4 font-medium text-brand-800">{doc.filename}</td>
                <td className="py-2 pr-4 text-brand-600">
                  {docTypeLabels[doc.doc_type] || doc.doc_type}
                </td>
                <td className="py-2 pr-4 text-xs text-brand-500">
                  {formatDateTime(doc.deleted_at)}
                </td>
                <td className="py-2 pr-4">
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${
                    daysLeft <= 7
                      ? 'bg-red-100 text-red-700'
                      : daysLeft <= 14
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-brand-100 text-brand-600'
                  }`}>
                    {t('documents.timeRemaining', { days: daysLeft })}
                  </span>
                </td>
                <td className="py-2 pr-4">
                  <button
                    type="button"
                    onClick={() => onRestore(doc.id)}
                    disabled={restoring === doc.id}
                    className="rounded-md border border-brand-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
                  >
                    {restoring === doc.id ? t('common.processing') : t('documents.restore')}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DocumentDetail — painel de detalhe com botoes de download
// ---------------------------------------------------------------------------

function DocumentDetail({
  detail,
  onClose,
  onDownload,
  onProvenance,
  onDelete,
  isAdmin,
  userId,
}: {
  detail: DocumentDetailResponse;
  onClose: () => void;
  onDownload: (format: 'json' | 'xml' | 'pdf' | 'fields') => void;
  onProvenance: (prov: Provenance | null) => void;
  onDelete: (doc: DocumentSummary) => void;
  isAdmin: boolean;
  userId?: string;
}) {
  const t = useT();
  const { formatDateTime: fmtDateTime, formatConfidence: fmtConfidence } = useFormatters();
  const docTypeLabels = getDocTypeLabels(t);
  const { document: doc, pipeline_result, lifecycle_events } = detail;
  // Issue #276: pipeline_result pode estar vazio para documentos pendente/
  // processando que ainda nao foram processados pelo pipeline assincrono.
  const root = pipeline_result?.root ?? ({} as Partial<DocumentResultData>);
  const fieldEntries = Object.entries(root.fields ?? {});
  const isProcessing = doc.status === 'pendente' || doc.status === 'processando';
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleDownload = useCallback(
    async (format: 'json' | 'xml' | 'pdf' | 'fields') => {
      setDownloading(format);
      try {
        await onDownload(format);
      } finally {
        setDownloading(null);
      }
    },
    [onDownload],
  );

  return (
    <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-brand-900">{doc.filename}</h2>
        <div className="flex items-center gap-3">
          {(isAdmin || doc.uploaded_by === userId) && (
            <button
              type="button"
              onClick={() => onDelete(doc)}
              className="text-red-500 hover:text-red-700"
              title={t('documents.delete')}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
            </button>
          )}
          <button type="button" onClick={onClose} className="text-sm text-brand-600 hover:text-brand-900">
            {t('documents.detail.close')}
          </button>
        </div>
      </div>

      {/* Download buttons (Issue #182) */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => handleDownload('json')}
          disabled={downloading === 'json'}
          className="rounded-md border border-brand-300 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
        >
          {downloading === 'json' ? t('documents.detail.downloading') : t('documents.detail.downloadJson')}
        </button>
        <button
          type="button"
          onClick={() => handleDownload('xml')}
          disabled={downloading === 'xml'}
          className="rounded-md border border-brand-300 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
        >
          {downloading === 'xml' ? t('documents.detail.downloading') : t('documents.detail.downloadXml')}
        </button>
        <button
          type="button"
          onClick={() => handleDownload('pdf')}
          disabled={downloading === 'pdf'}
          className="rounded-md border border-brand-300 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
        >
          {downloading === 'pdf' ? t('documents.detail.downloading') : t('documents.detail.downloadPdf')}
        </button>
        <button
          type="button"
          onClick={() => handleDownload('fields')}
          disabled={downloading === 'fields'}
          className="rounded-md border border-brand-300 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
        >
          {downloading === 'fields' ? t('documents.detail.downloading') : t('documents.detail.downloadFields')}
        </button>
      </div>

      {/* Summary */}
      <div className="mb-6 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 lg:grid-cols-5">
        <div className="rounded bg-brand-100 p-3 ring-1 ring-brand-300">
          <span className="text-xs text-brand-600">{t('documents.detail.importId')}</span>
          <p className="font-mono text-sm font-semibold text-brand-900">{doc.import_id || '—'}</p>
        </div>
        <div className="rounded bg-brand-50 p-3">
          <span className="text-xs text-brand-500">{t('documents.detail.type')}</span>
          <p className="font-medium text-brand-800">{docTypeLabels[doc.doc_type] || doc.doc_type}</p>
        </div>
        <div className="rounded bg-brand-50 p-3">
          <span className="text-xs text-brand-500">{t('documents.detail.origin')}</span>
          <p className="font-medium text-brand-800">
            {doc.provenance ? (
              <button
                type="button"
                onClick={() => onProvenance(doc.provenance)}
                className="cursor-pointer text-left underline hover:text-brand-900"
                title={doc.source_path || ''}
              >
                {provenanceSummary(doc.provenance)}
              </button>
            ) : (
              doc.source_type
            )}
          </p>
        </div>
        <div className="rounded bg-brand-50 p-3">
          <span className="text-xs text-brand-500">{t('documents.detail.status')}</span>
          <p className="font-medium text-brand-800">{doc.status}</p>
        </div>
        <div className="rounded bg-brand-50 p-3">
          <span className="text-xs text-brand-500">{t('documents.detail.avgConfidence')}</span>
          <p className="font-medium text-brand-800">
            {doc.avg_confidence > 0 ? fmtConfidence(doc.avg_confidence) : '—'}
          </p>
        </div>
      </div>

      {/* Processamento: datas de upload, inicio e conclusao (Issue #343) */}
      <div className="mb-6 rounded-lg border border-brand-200 bg-brand-50/50 p-4">
        <h3 className="mb-3 text-sm font-semibold text-brand-800">{t('documents.detail.processing')}</h3>
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div className="rounded bg-white p-3 ring-1 ring-brand-200">
            <span className="text-xs text-brand-500">{t('documents.cards.date')}</span>
            <p className="font-medium text-brand-800">{fmtDateTime(doc.created_at)}</p>
          </div>
          <div className="rounded bg-white p-3 ring-1 ring-brand-200">
            <span className="text-xs text-brand-500">{t('documents.detail.processingStartedAt')}</span>
            <p className="font-medium text-brand-800">{fmtDateTime(doc.processing_started_at)}</p>
          </div>
          <div className="rounded bg-white p-3 ring-1 ring-brand-200">
            <span className="text-xs text-brand-500">{t('documents.detail.processedAt')}</span>
            <p className="font-medium text-brand-800">{fmtDateTime(doc.processed_at)}</p>
          </div>
        </div>
      </div>

      {/* Processing steps (Issue #268) */}
      {doc.processing_steps && doc.processing_steps.length > 0 && (
        <div className="mb-6">
          <h3 className="mb-2 text-sm font-semibold text-brand-800">{t('documents.detail.processingSteps')}</h3>
          <div className="flex flex-wrap gap-2">
            {doc.processing_steps.map((step) => (
              <span
                key={step}
                className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700"
                title={step}
              >
                {step}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Review warning */}
      {root.needs_review && (
        <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          {t('documents.detail.needsReview', { reason: root.review_reason || t('documents.detail.reviewReasonDefault') })}
        </div>
      )}

      {root.error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {t('documents.detail.error', { error: root.error })}
        </div>
      )}

      {/* Issue #276: estado de processamento para documentos pendente/processando
          que ainda nao tem pipeline_result preenchido. */}
      {isProcessing && (
        <div className="mb-6 flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          <svg className="h-5 w-5 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <div>
            <p className="font-medium">
              {doc.status === 'pendente'
                ? 'Documento na fila de processamento'
                : 'Processando documento'}
            </p>
            <p className="text-xs text-blue-600">
              Os campos extraidos apareceram aqui assim que o pipeline concluir.
            </p>
          </div>
        </div>
      )}

      {/* Fields */}
      {fieldEntries.length > 0 && (
        <div className="mb-6">
          <h3 className="mb-2 font-semibold text-brand-800">{t('documents.detail.extractedFields')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-brand-200 text-brand-700">
                <tr>
                  <th className="py-2 pr-3">{t('documents.detail.col.field')}</th>
                  <th className="py-2 pr-3">{t('documents.detail.col.value')}</th>
                  <th className="py-2 pr-3">{t('documents.detail.col.confidence')}</th>
                  <th className="py-2 pr-3">{t('documents.detail.col.origin')}</th>
                  <th className="py-2 pr-3">{t('documents.detail.col.raw')}</th>
                </tr>
              </thead>
              <tbody>
                {fieldEntries.map(([name, field]) => (
                  <tr key={name} className="border-b border-brand-100">
                    <td className="py-2 pr-3 font-medium text-brand-800">
                      {getFieldLabel(name, t, root.document_type)}
                      {field.corrected && (
                        <span className="ml-1 rounded bg-blue-100 px-1 text-[10px] font-medium text-blue-700">
                          {t('common.corrected')}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-brand-700">
                      {field.value !== null && field.value !== undefined ? String(field.value) : '—'}
                    </td>
                    <td className="py-2 pr-3">
                      <ConfidenceBar value={field.confidence} />
                    </td>
                    <td className="py-2 pr-3 text-xs text-brand-500">{field.source || '—'}</td>
                    <td className="py-2 pr-3 text-xs text-brand-400 max-w-xs truncate">
                      {field.raw || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Metadata */}
      {Object.keys(root.metadata ?? {}).length > 0 && (
        <div className="mb-6">
          <h3 className="mb-2 font-semibold text-brand-800">{t('documents.detail.metadata')}</h3>
          <pre className="overflow-x-auto rounded bg-brand-50 p-3 text-xs text-brand-700">
            {JSON.stringify(root.metadata ?? {}, null, 2)}
          </pre>
        </div>
      )}

      {/* Children */}
      {(root.children ?? []).length > 0 && (
        <div className="mb-6">
          <h3 className="mb-2 font-semibold text-brand-800">
            {t('documents.detail.children', { count: (root.children ?? []).length })}
          </h3>
          <div className="space-y-2">
            {(root.children ?? []).map((child, i) => (
              <div key={i} className="rounded border border-brand-100 p-3 text-sm">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-brand-800">{child.file}</span>
                  <span className="text-xs text-brand-500">{child.document_type}</span>
                  <span className="text-xs text-brand-500">{child.source_type}</span>
                  {child.error && <span className="text-xs text-red-600">{t('documents.detail.childError')}</span>}
                </div>
                {Object.keys(child.fields).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2 text-xs">
                    {Object.entries(child.fields).map(([k, v]) => (
                      <span key={k} className="rounded bg-brand-50 px-2 py-0.5 text-brand-600">
                        {getFieldLabel(k, t, child.document_type)}: {v.value !== null ? String(v.value) : '—'}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lifecycle events */}
      {lifecycle_events.length > 0 && (
        <div>
          <h3 className="mb-2 font-semibold text-brand-800">{t('documents.detail.processingLog')}</h3>
          <div className="space-y-1">
            {lifecycle_events.map((evt) => (
              <div key={evt.id} className="flex items-center gap-3 text-sm text-brand-600">
                <span className="text-xs text-brand-400">{fmtDateTime(evt.created_at)}</span>
                <span className="rounded bg-brand-100 px-2 py-0.5 text-xs text-brand-700">
                  {evt.event}
                </span>
                {evt.justification && <span className="text-xs text-brand-500">{evt.justification}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-16 overflow-hidden rounded-full bg-brand-100">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-brand-600">{pct}%</span>
    </div>
  );
}

export default Documents;
